/**
 * SAT Transformation Mastery™ · Consumer Page (STEP 3)
 * Zeigt: Status der 5 Säulen · finalen TMS 0–100 · 4-Block-Synthese · PDF.
 * Feature-Flag-gated. Reuse: Gold/Navy-Palette · Slider-Muster nicht nötig.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("sat_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const PILLARS = [
  { key: "hpa",      label: "HPA · Potenzial" },
  { key: "aura",     label: "AURA · Muster" },
  { key: "htc",      label: "HTC · Transformation" },
  { key: "circle",   label: "Circle · Lebensbalance" },
  { key: "survival", label: "Survival · Lebensfähigkeit" },
];

export default function TransformationMastery() {
  const [sources, setSources]     = useState(null);
  const [preview, setPreview]     = useState(null);
  const [envelope, setEnvelope]   = useState(null);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  async function loadAll() {
    try {
      const s = await axios.get(`${API}/api/tm_core/status`,   { headers: authHeaders() });
      setSources(s.data.sources);
      const p = await axios.get(`${API}/api/tm_core/preview`,  { headers: authHeaders() });
      setPreview(p.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Transformation Mastery ist derzeit nicht verfügbar.");
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function releaseNow() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/tm_core/release`, {}, { headers: authHeaders() });
      setEnvelope(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setLoading(false);
  }

  function downloadPdf() {
    window.open(`${API}/api/tm_core/report/pdf`, "_blank");
  }

  const view = envelope || preview;
  const isComplete = view && view.score_status === "COMPLETE";

  return (
    <div className="min-h-screen bg-[#08102A] text-slate-100" data-testid="mastery-page">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <p className="mb-2 text-xs uppercase tracking-widest text-[#D4AF37]">Transformation Mastery™</p>
        <h1 className="mb-6 text-4xl font-light text-[#F6E7B8] sm:text-5xl">
          Dein Gesamtbild.
        </h1>
        <p className="mb-10 max-w-2xl text-slate-300">
          Deine fünf Säulen zusammengeführt zu einem einzigen, transparenten Wert:
          dem <span className="text-[#D4AF37]">Transformation Mastery Score</span>.
        </p>

        {error && (
          <div className="mb-6 rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
               data-testid="mastery-error">{error}</div>
        )}

        {/* Status der 5 Säulen */}
        {sources && (
          <div className="mb-8 grid grid-cols-1 gap-2 sm:grid-cols-5">
            {PILLARS.map(p => (
              <div key={p.key} data-testid={`mastery-pillar-${p.key}`}
                   className={`rounded-lg border px-3 py-3 text-xs ${
                     sources[p.key]
                       ? "border-[#D4AF37]/40 bg-slate-900/40 text-[#F6E7B8]"
                       : "border-slate-700 bg-slate-900/20 text-slate-500"}`}>
                <div className="mb-1 flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${sources[p.key] ? "bg-[#D4AF37]" : "bg-slate-600"}`}></span>
                  <span className="font-medium">{sources[p.key] ? "READY" : "PENDING"}</span>
                </div>
                {p.label}
              </div>
            ))}
          </div>
        )}

        {/* TMS Hero */}
        {view && (
          <div className="mb-8 rounded-2xl border border-[#D4AF37]/30 bg-slate-900/40 p-8"
               data-testid="mastery-hero">
            <p className="text-sm uppercase tracking-widest text-[#D4AF37]">Transformation Mastery Score</p>
            <p className="mt-2 text-7xl font-light text-white">
              {isComplete ? view.transformation_mastery_score : "—"}
              <span className="text-2xl text-slate-500">/100</span>
            </p>
            {isComplete && view.synthesis && (
              <p className="mt-3 text-lg text-[#F6E7B8]">{view.synthesis.overall_band}</p>
            )}
            {!isComplete && (
              <p className="mt-3 text-sm text-slate-400">
                Der Score wird erst berechnet, wenn alle fünf Säulen freigegeben sind.
              </p>
            )}
          </div>
        )}

        {/* Komponenten sichtbar (Transparenz) */}
        {view && view.components && (
          <div className="mb-8 rounded-xl border border-slate-800 bg-slate-900/30 p-5"
               data-testid="mastery-components">
            <p className="mb-3 text-xs uppercase tracking-widest text-slate-400">Deine fünf Komponenten</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
              {[
                ["HPA",      view.components.s_hpa],
                ["AURA",     view.components.s_aura],
                ["HTC",      view.components.s_htc],
                ["Circle",   view.components.s_circle],
                ["Survival", view.components.s_survival],
              ].map(([label, val]) => (
                <div key={label} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-center">
                  <p className="text-xs uppercase tracking-widest text-slate-400">{label}</p>
                  <p className="mt-1 text-2xl text-[#F6E7B8]">{val}<span className="text-xs text-slate-500">/100</span></p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4-Block Synthese */}
        {isComplete && view.synthesis && (
          <div className="mb-8 grid gap-4 sm:grid-cols-2" data-testid="mastery-synthesis">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Was läuft bereits gut</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-200">
                {view.synthesis.strengths.map(s => <li key={s}>• {s}</li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Dein stärkstes Entwicklungspotenzial</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-200">
                {view.synthesis.development_areas.map(s => <li key={s}>• {s}</li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-[#D4AF37]/40 bg-slate-900/50 p-5 sm:col-span-2">
              <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Dein nächster sinnvoller Schritt</p>
              <p className="mt-2 text-base text-[#F6E7B8]">{view.synthesis.next_focus}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={releaseNow} disabled={loading}
                  data-testid="mastery-release-btn"
                  className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-medium text-[#08102A] hover:bg-[#F6E7B8] disabled:opacity-50">
            {loading ? "…" : "Auswertung freigeben"}
          </button>
          <button onClick={downloadPdf}
                  data-testid="mastery-pdf-btn"
                  className="rounded-full border border-[#D4AF37]/60 px-6 py-3 text-sm font-medium text-[#F6E7B8] hover:bg-slate-900/60">
            PDF herunterladen
          </button>
        </div>

        {view && (
          <p className="mt-8 text-xs text-slate-500">
            {view.export_hash ? `Envelope: ${view.envelope_version || "—"} · Hash: ${(view.export_hash || "").slice(0, 12)}…` : ""}
          </p>
        )}
      </div>
    </div>
  );
}
